/**
 * Service de gestion des permissions unifié
 * Utilise les permissions du backend pour une cohérence totale
 */

// Mapping des rôles backend vers les rôles frontend
export const ROLE_MAPPING = {
    ADMIN: "ROLE_ADMIN",
    MANAGER: "ROLE_MANAGER",
    TECHNICIEN: "ROLE_TECHNICIEN",
    CLIENT: "ROLE_CLIENT",
};

// Permissions par ressource
export const PERMISSIONS = {
    COLLABORATEURS: "collaborateurs",
    CLIENTS: "clients",
    CONTACTS: "contacts",
    TICKETS: "tickets",
    MONITORING: "monitoring",
    PROFILE: "profile",
    SAUVEGARDES: "sauvegardes",
};

// Actions possibles
export const ACTIONS = {
    VIEW: "view",
    CREATE: "create",
    EDIT: "edit",
    DELETE: "delete",
    STATUS_CHANGE: "status_change",
    ASSIGN: "assign",
};

/**
 * Vérifie si l'utilisateur a une permission spécifique
 * @param {Object} user - Utilisateur avec ses permissions
 * @param {string} resource - Ressource (ex: 'tickets', 'clients')
 * @param {string} action - Action (ex: 'view', 'create', 'edit')
 * @returns {boolean}
 */
export function hasPermission(user, resource, action) {
    if (!user || !user.permissions) {
        return false;
    }

    // Vérifier les permissions détaillées
    if (
        user.permissions.allPermissions &&
        user.permissions.allPermissions[resource]
    ) {
        return user.permissions.allPermissions[resource].includes(action);
    }

    // Fallback sur les permissions booléennes
    const permissionKey = `can${capitalize(resource)}${capitalize(action)}`;
    return user.permissions[permissionKey] === true;
}

/**
 * Vérifie si l'utilisateur peut gérer une ressource
 * @param {Object} user - Utilisateur
 * @param {string} resource - Ressource à gérer
 * @returns {boolean}
 */
export function canManage(user, resource) {
    return (
        hasPermission(user, resource, ACTIONS.CREATE) &&
        hasPermission(user, resource, ACTIONS.EDIT)
    );
}

/**
 * Vérifie si l'utilisateur est un collaborateur
 * @param {Object} user - Utilisateur
 * @returns {boolean}
 */
export function isCollaborateur(user) {
    return user && user.type === "collaborateur";
}

/**
 * Vérifie si l'utilisateur est un client
 * @param {Object} user - Utilisateur
 * @returns {boolean}
 */
export function isClient(user) {
    return user && user.type === "contact";
}

/**
 * Vérifie si l'utilisateur est admin
 * @param {Object} user - Utilisateur
 * @returns {boolean}
 */
export function isAdmin(user) {
    return user && user.backendRole === "ADMIN";
}

/**
 * Vérifie si l'utilisateur est manager
 * @param {Object} user - Utilisateur
 * @returns {boolean}
 */
export function isManager(user) {
    return user && user.backendRole === "MANAGER";
}

/**
 * Vérifie si l'utilisateur est technicien
 * @param {Object} user - Utilisateur
 * @returns {boolean}
 */
export function isTechnicien(user) {
    return user && user.backendRole === "TECHNICIEN";
}

/**
 * Vérifie si l'utilisateur peut accéder au monitoring
 * @param {Object} user - Utilisateur
 * @returns {boolean}
 */
export function canViewMonitoring(user) {
    return hasPermission(user, PERMISSIONS.MONITORING, ACTIONS.VIEW);
}

/**
 * Vérifie si l'utilisateur peut gérer les utilisateurs
 * @param {Object} user - Utilisateur
 * @returns {boolean}
 */
export function canManageUsers(user) {
    return user && user.permissions && user.permissions.canManageUsers;
}

/**
 * Vérifie si l'utilisateur peut gérer les clients
 * @param {Object} user - Utilisateur
 * @returns {boolean}
 */
export function canManageClients(user) {
    return user && user.permissions && user.permissions.canManageClients;
}

/**
 * Vérifie si l'utilisateur peut gérer les contacts
 * @param {Object} user - Utilisateur
 * @returns {boolean}
 */
export function canManageContacts(user) {
    return user && user.permissions && user.permissions.canManageContacts;
}

/**
 * Vérifie si l'utilisateur peut gérer les collaborateurs
 * @param {Object} user - Utilisateur
 * @returns {boolean}
 */
export function canManageCollaborateurs(user) {
    return user && user.permissions && user.permissions.canManageCollaborateurs;
}

/**
 * Vérifie si l'utilisateur peut voir les tickets
 * @param {Object} user - Utilisateur
 * @returns {boolean}
 */
export function canViewTickets(user) {
    return user && user.permissions && user.permissions.canViewTickets;
}

/**
 * Vérifie si l'utilisateur peut créer des tickets
 * @param {Object} user - Utilisateur
 * @returns {boolean}
 */
export function canCreateTickets(user) {
    return user && user.permissions && user.permissions.canCreateTickets;
}

/**
 * Vérifie si l'utilisateur peut modifier les tickets
 * @param {Object} user - Utilisateur
 * @returns {boolean}
 */
export function canEditTickets(user) {
    return user && user.permissions && user.permissions.canEditTickets;
}

/**
 * Vérifie si l'utilisateur peut supprimer les tickets
 * @param {Object} user - Utilisateur
 * @returns {boolean}
 */
export function canDeleteTickets(user) {
    return user && user.permissions && user.permissions.canDeleteTickets;
}

/**
 * Vérifie si l'utilisateur peut changer le statut des tickets
 * @param {Object} user - Utilisateur
 * @returns {boolean}
 */
export function canChangeTicketStatus(user) {
    return user && user.permissions && user.permissions.canChangeTicketStatus;
}

/**
 * Vérifie si l'utilisateur peut assigner des tickets
 * @param {Object} user - Utilisateur
 * @returns {boolean}
 */
export function canAssignTickets(user) {
    return user && user.permissions && user.permissions.canAssignTickets;
}

/**
 * Obtient le rôle backend de l'utilisateur
 * @param {Object} user - Utilisateur
 * @returns {string}
 */
export function getUserBackendRole(user) {
    return user ? user.backendRole : "CLIENT";
}

/**
 * Obtient le rôle Symfony de l'utilisateur
 * @param {Object} user - Utilisateur
 * @returns {string}
 */
export function getUserSymfonyRole(user) {
    return user ? user.symfonyRole : "ROLE_CLIENT";
}

/**
 * Obtient toutes les permissions de l'utilisateur
 * @param {Object} user - Utilisateur
 * @returns {Object}
 */
export function getAllPermissions(user) {
    return user && user.permissions ? user.permissions.allPermissions : {};
}

/**
 * Vérifie si l'utilisateur peut voir les sauvegardes
 * @param {Object} user - Utilisateur
 * @returns {boolean}
 */
export function canViewSauvegardes(user) {
    return hasPermission(user, PERMISSIONS.SAUVEGARDES, ACTIONS.VIEW);
}

/**
 * Utilitaires
 */
function capitalize(str) {
    return str.charAt(0).toUpperCase() + str.slice(1);
}

// Assign the object to a variable before exporting it as default
const permissionsService = {
    hasPermission,
    canManage,
    isCollaborateur,
    isClient,
    isAdmin,
    isManager,
    isTechnicien,
    canViewMonitoring,
    canManageUsers,
    canManageClients,
    canManageContacts,
    canManageCollaborateurs,
    canViewTickets,
    canCreateTickets,
    canEditTickets,
    canDeleteTickets,
    canChangeTicketStatus,
    canAssignTickets,
    getUserBackendRole,
    getUserSymfonyRole,
    getAllPermissions,
    PERMISSIONS,
    ACTIONS,
    ROLE_MAPPING,
    canViewSauvegardes,
};

export default permissionsService;
